<template>
  <div
    class="phone-number-input"
    :class="{
      'has-error': hasError,
      'is-disabled': disabled,
      'is-dropdown-open': isCountryDropdownOpen,
    }"
  >
    <label v-if="label" :class="labelClass || 'phone-number-label'">
      {{ label }}<span v-if="required" :class="requiredClass || 'phone-number-required'">*</span>
    </label>
    
    <div class="phone-number-wrapper">
      <!-- Country Code Selector -->
      <div 
        class="phone-number-country-selector" 
        :class="{ 'is-open': isCountryDropdownOpen }"
        @click.stop="toggleCountryDropdown"
      >
        <div class="phone-number-country-display">
          <span class="phone-number-flag">
            <img 
              :src="getFlagUrl(selectedCountry.code)" 
              :alt="selectedCountry.name"
              class="phone-number-flag-img"
            />
          </span>
          <span class="phone-number-code">+{{ selectedCountry.dialCode }}</span>
          <i 
            class="bi bi-chevron-down phone-number-dropdown-arrow"
            :class="{ 'is-rotated': isCountryDropdownOpen }"
          ></i>
        </div>
        
        <!-- Country Dropdown (teleported so parent overflow/transform cannot clip or misposition it) -->
        <Teleport to="body">
          <div 
            v-if="isCountryDropdownOpen" 
            ref="countryDropdown"
            class="phone-number-country-dropdown"
            @click.stop
            @mousedown.prevent
          >
            <div class="phone-number-country-search">
              <div class="phone-number-country-search__field">
                <span class="phone-number-country-search__icon" aria-hidden="true">
                  <i class="bi bi-search"></i>
                </span>
                <input
                  ref="countrySearchInput"
                  class="phone-number-country-search-input"
                  v-model="countrySearchQuery"
                  type="text"
                  placeholder="Search country..."
                  autocomplete="off"
                  @input="filterCountries"
                  @keydown="handleCountryKeydown"
                />
              </div>
            </div>
            
            <div class="phone-number-country-list">
              <div
                v-for="country in filteredCountries"
                :key="country.code"
                class="phone-number-country-option"
                :class="{ 'is-selected': country.code === selectedCountry.code }"
                @click="selectCountry(country)"
              >
                <span class="phone-number-flag">
                  <img 
                    :src="getFlagUrl(country.code)" 
                    :alt="country.name"
                    class="phone-number-flag-img"
                  />
                </span>
                <span class="phone-number-country-name">{{ country.name }}</span>
                <span class="phone-number-dial-code">+{{ country.dialCode }}</span>
              </div>
              <div v-if="filteredCountries.length === 0" class="phone-number-no-results">
                No countries found
              </div>
            </div>
          </div>
        </Teleport>
      </div>
      
      <!-- Phone Number Input -->
      <input
        ref="phoneInput"
        type="tel"
        class="phone-number-field"
        :value="phoneNumber"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :class="{ 'has-error': hasError }"
        @input="handlePhoneInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />
    </div>
    
    <!-- Error Message -->
    <div v-if="hasError && errorMessage" class="phone-number-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { parsePhoneNumber, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';

// Country phone number specifications: { minLength, maxLength, format }
const COUNTRY_PHONE_SPECS = {
  'US': { min: 10, max: 10, format: 'xxx-xxx-xxxx' },
  'CA': { min: 10, max: 10, format: 'xxx-xxx-xxxx' },
  'GB': { min: 10, max: 10, format: 'xxxx xxxxxx' },
  'IN': { min: 10, max: 10, format: 'xxxxx-xxxxx' },
  'AU': { min: 9, max: 9, format: 'xxxx xxxx' },
  'CN': { min: 11, max: 11, format: 'xxx xxxx xxxx' },
  'DE': { min: 10, max: 11, format: 'xxxx xxxxxx' },
  'FR': { min: 9, max: 9, format: 'x xx xx xx xx' },
  'IT': { min: 9, max: 10, format: 'xxx xxx xxxx' },
  'ES': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'BR': { min: 10, max: 11, format: 'xx xxxxx-xxxx' },
  'MX': { min: 10, max: 10, format: 'xx xxxx xxxx' },
  'JP': { min: 10, max: 11, format: 'xx-xxxx-xxxx' },
  'KR': { min: 9, max: 11, format: 'xxx-xxxx-xxxx' },
  'RU': { min: 10, max: 10, format: 'xxx xxx-xx-xx' },
  'NL': { min: 9, max: 9, format: 'xx xxxxxxxx' },
  'SE': { min: 9, max: 9, format: 'xxx-xxx xx xx' },
  'NO': { min: 8, max: 8, format: 'xxx xx xxx' },
  'DK': { min: 8, max: 8, format: 'xx xx xx xx' },
  'FI': { min: 9, max: 9, format: 'xxx xxx xxxx' },
  'CH': { min: 9, max: 9, format: 'xx xxx xx xx' },
  'AT': { min: 10, max: 13, format: 'xxxx xxxxxxx' },
  'BE': { min: 9, max: 9, format: 'xxxx xx xxx' },
  'PL': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'GR': { min: 10, max: 10, format: 'xxx xxx xxxx' },
  'PT': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'IE': { min: 9, max: 9, format: 'xx xxx xxxx' },
  'CZ': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'HU': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'RO': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'SG': { min: 8, max: 8, format: 'xxxx xxxx' },
  'MY': { min: 9, max: 10, format: 'xxx-xxx xxxx' },
  'TH': { min: 9, max: 9, format: 'xxx-xxx-xxx' },
  'VN': { min: 9, max: 10, format: 'xxx xxx xxxx' },
  'PH': { min: 10, max: 10, format: 'xxx xxx xxxx' },
  'ID': { min: 9, max: 11, format: 'xxx-xxx-xxxx' },
  'PK': { min: 10, max: 10, format: 'xxx-xxxxxxx' },
  'BD': { min: 10, max: 10, format: 'xxxx-xxxxxx' },
  'AE': { min: 9, max: 9, format: 'xx xxx xxxx' },
  'SA': { min: 9, max: 9, format: 'xx xxx xxxx' },
  'IL': { min: 9, max: 9, format: 'xx-xxx-xxxx' },
  'TR': { min: 10, max: 10, format: 'xxx xxx xx xx' },
  'ZA': { min: 9, max: 9, format: 'xx xxx xxxx' },
  'EG': { min: 10, max: 10, format: 'xxx xxx xxxx' },
  'NG': { min: 10, max: 10, format: 'xxx xxx xxxx' },
  'AR': { min: 10, max: 10, format: 'xx xxxx-xxxx' },
  'CL': { min: 9, max: 9, format: 'x xxxx xxxx' },
  'CO': { min: 10, max: 10, format: 'xxx xxxxxxx' },
  'PE': { min: 9, max: 9, format: 'xxx xxx xxx' },
  'VE': { min: 10, max: 10, format: 'xxx-xxx.xxxx' },
  'NZ': { min: 8, max: 10, format: 'xxx xxxx' }
};

// Country data with dial codes (flags now loaded from package)
const COUNTRIES = [
  { code: 'US', name: 'United States', dialCode: '1' },
  { code: 'CN', name: 'China (中国)', dialCode: '86' },
  { code: 'FR', name: 'France', dialCode: '33' },
  { code: 'IN', name: 'India (भारत)', dialCode: '91' },
  { code: 'GB', name: 'United Kingdom', dialCode: '44' },
  { code: 'CA', name: 'Canada', dialCode: '1' },
  { code: 'AU', name: 'Australia', dialCode: '61' },
  { code: 'DE', name: 'Germany', dialCode: '49' },
  { code: 'JP', name: 'Japan', dialCode: '81' },
  { code: 'BR', name: 'Brazil', dialCode: '55' },
  { code: 'MX', name: 'Mexico', dialCode: '52' },
  { code: 'IT', name: 'Italy', dialCode: '39' },
  { code: 'ES', name: 'Spain', dialCode: '34' },
  { code: 'RU', name: 'Russia', dialCode: '7' },
  { code: 'KR', name: 'South Korea', dialCode: '82' },
  { code: 'NL', name: 'Netherlands', dialCode: '31' },
  { code: 'SE', name: 'Sweden', dialCode: '46' },
  { code: 'NO', name: 'Norway', dialCode: '47' },
  { code: 'DK', name: 'Denmark', dialCode: '45' },
  { code: 'FI', name: 'Finland', dialCode: '358' },
  { code: 'CH', name: 'Switzerland', dialCode: '41' },
  { code: 'AT', name: 'Austria', dialCode: '43' },
  { code: 'BE', name: 'Belgium', dialCode: '32' },
  { code: 'PL', name: 'Poland', dialCode: '48' },
  { code: 'GR', name: 'Greece', dialCode: '30' },
  { code: 'PT', name: 'Portugal', dialCode: '351' },
  { code: 'IE', name: 'Ireland', dialCode: '353' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '420' },
  { code: 'HU', name: 'Hungary', dialCode: '36' },
  { code: 'RO', name: 'Romania', dialCode: '40' },
  { code: 'SG', name: 'Singapore', dialCode: '65' },
  { code: 'MY', name: 'Malaysia', dialCode: '60' },
  { code: 'TH', name: 'Thailand', dialCode: '66' },
  { code: 'VN', name: 'Vietnam', dialCode: '84' },
  { code: 'PH', name: 'Philippines', dialCode: '63' },
  { code: 'ID', name: 'Indonesia', dialCode: '62' },
  { code: 'PK', name: 'Pakistan', dialCode: '92' },
  { code: 'BD', name: 'Bangladesh', dialCode: '880' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '971' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '966' },
  { code: 'IL', name: 'Israel', dialCode: '972' },
  { code: 'TR', name: 'Turkey', dialCode: '90' },
  { code: 'ZA', name: 'South Africa', dialCode: '27' },
  { code: 'EG', name: 'Egypt', dialCode: '20' },
  { code: 'NG', name: 'Nigeria', dialCode: '234' },
  { code: 'AR', name: 'Argentina', dialCode: '54' },
  { code: 'CL', name: 'Chile', dialCode: '56' },
  { code: 'CO', name: 'Colombia', dialCode: '57' },
  { code: 'PE', name: 'Peru', dialCode: '51' },
  { code: 'VE', name: 'Venezuela', dialCode: '58' },
  { code: 'NZ', name: 'New Zealand', dialCode: '64' }
];

export default {
  name: 'PhoneNumberInput',
  // Declare so parent @input receives emitted string, not bubbled native InputEvent from inner <input>
  emits: ['input', 'change', 'focus', 'blur', 'keydown', 'country-change'],
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Phone number'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    defaultCountry: {
      type: String,
      default: 'US'
    },
    errorMessage: {
      type: String,
      default: ''
    },
    /** When set (e.g. parent form label class), replaces default .phone-number-label for consistent typography */
    labelClass: {
      type: String,
      default: ''
    },
    /** When set, replaces default .phone-number-required (use with labelClass in themed forms) */
    requiredClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      phoneNumber: '',
      countryCode: this.defaultCountry || 'US',
      isCountryDropdownOpen: false,
      countrySearchQuery: '',
      highlightedCountryIndex: -1,
      countries: COUNTRIES,
      /** Skip value watcher when parent echoes our own $emit (controlled input loop) */
      suppressValueWatch: false,
      /** Avoid N× document/window listeners when many instances mount (e.g. settings lists) */
      dropdownListenersBound: false
    };
  },
  computed: {
    selectedCountry() {
      return this.countries.find(c => c.code === this.countryCode) || this.countries[0];
    },
    filteredCountries() {
      if (!this.countrySearchQuery.trim()) {
        return this.countries;
      }
      const query = this.countrySearchQuery.toLowerCase();
      return this.countries.filter(country => 
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    },
    hasError() {
      // Only show error if there's an explicit error message
      // Don't show validation error for required fields unless explicitly validated
      return !!this.errorMessage;
    },
    fullPhoneNumber() {
      if (!this.phoneNumber || !this.phoneNumber.trim()) return '';
      const digits = this.phoneNumber.replace(/\D/g, '');
      if (!digits) return '';
      return `+${this.selectedCountry.dialCode}${digits}`;
    }
  },
  watch: {
    value(newVal) {
      if (this.suppressValueWatch) return;
      const prop = newVal === null || newVal === undefined ? '' : String(newVal).trim();
      const current = this.fullPhoneNumber || '';
      if (prop !== current) {
        this.parsePhoneNumber(newVal);
      }
    }
  },
  mounted() {
    this.parsePhoneNumber(this.value);
  },
  beforeUnmount() {
    this.teardownDropdownListeners();
  },
  methods: {
    setupDropdownListeners() {
      if (this.dropdownListenersBound) return;
      this.dropdownListenersBound = true;
      document.addEventListener('click', this.handleClickOutside);
      window.addEventListener('scroll', this.handleScroll, true);
      window.addEventListener('resize', this.handleResize);
    },
    teardownDropdownListeners() {
      if (!this.dropdownListenersBound) return;
      this.dropdownListenersBound = false;
      document.removeEventListener('click', this.handleClickOutside);
      window.removeEventListener('scroll', this.handleScroll, true);
      window.removeEventListener('resize', this.handleResize);
    },
    emitValueToParent(fullE164) {
      const v = fullE164 === null || fullE164 === undefined ? '' : String(fullE164);
      this.suppressValueWatch = true;
      this.$emit('input', v);
      this.$emit('change', v);
      this.$nextTick(() => {
        this.suppressValueWatch = false;
      });
    },
    parsePhoneNumber(phoneValue) {
      const raw =
        phoneValue === null || phoneValue === undefined || phoneValue === ''
          ? ''
          : String(phoneValue).trim();
      if (!raw) {
        this.phoneNumber = '';
        return;
      }

      try {
        // Use libphonenumber-js to parse the phone number
        const phoneNumber = parsePhoneNumber(raw, { defaultCountry: this.countryCode });

        if (phoneNumber && phoneNumber.country) {
          this.countryCode = phoneNumber.country;
          this.phoneNumber = phoneNumber.formatNational();
        } else {
          // Fallback parsing
          const cleanPhone = raw.replace(/^\+/, '');
          const sortedCountries = [...this.countries].sort((a, b) => b.dialCode.length - a.dialCode.length);
          
          for (const country of sortedCountries) {
            if (cleanPhone.startsWith(country.dialCode)) {
              this.countryCode = country.code;
              const remainingNumber = cleanPhone.substring(country.dialCode.length);
              this.phoneNumber = this.formatPhoneNumber(remainingNumber, country.code);
              return;
            }
          }
          
          this.phoneNumber = this.formatPhoneNumber(cleanPhone, this.countryCode);
        }
      } catch (error) {
        // Fallback to manual parsing if libphonenumber fails
        const cleanPhone = raw.replace(/^\+/, '');
        const sortedCountries = [...this.countries].sort((a, b) => b.dialCode.length - a.dialCode.length);
        
        for (const country of sortedCountries) {
          if (cleanPhone.startsWith(country.dialCode)) {
            this.countryCode = country.code;
            const remainingNumber = cleanPhone.substring(country.dialCode.length);
            this.phoneNumber = this.formatPhoneNumber(remainingNumber, country.code);
            return;
          }
        }
        
        this.phoneNumber = this.formatPhoneNumber(cleanPhone, this.countryCode);
      }
    },
    formatPhoneNumber(number, countryCode) {
      // Remove all non-digits
      const digits = String(number == null ? '' : number).replace(/\D/g, '');
      const spec = COUNTRY_PHONE_SPECS[countryCode];
      
      if (!spec) {
        // Generic formatting for countries without spec
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
      }
      
      // Format based on country specification
      switch (countryCode) {
        case 'US':
        case 'CA':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        case 'IN':
          if (digits.length <= 5) return digits;
          return `${digits.slice(0, 5)}-${digits.slice(5, 10)}`;
        case 'GB':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        case 'AU':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        case 'CN':
          if (digits.length <= 3) return digits;
          if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
        case 'FR':
          if (digits.length <= 1) return digits;
          if (digits.length <= 3) return `${digits.slice(0, 1)} ${digits.slice(1)}`;
          if (digits.length <= 5) return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`;
          if (digits.length <= 7) return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
          return `${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
        case 'DE':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        case 'BR':
          if (digits.length <= 2) return digits;
          if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7)}`;
        case 'MX':
          if (digits.length <= 2) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        case 'JP':
          if (digits.length <= 2) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
          return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
        case 'IT':
        case 'ES':
        case 'PL':
        case 'PT':
        case 'CZ':
        case 'HU':
        case 'RO':
        case 'PE':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        case 'RU':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
        case 'PK':
          if (digits.length <= 3) return digits;
          return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        case 'BD':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)}-${digits.slice(4)}`;
        case 'TH':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        case 'ID':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        case 'IL':
          if (digits.length <= 2) return digits;
          if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
          return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
        case 'MY':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
        case 'TR':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
        case 'AR':
          if (digits.length <= 2) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
        case 'VE':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)}.${digits.slice(6)}`;
        case 'NO':
        case 'DK':
          if (digits.length <= 2) return digits;
          if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
          if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
        case 'NL':
        case 'BE':
          if (digits.length <= 2) return digits;
          return `${digits.slice(0, 2)} ${digits.slice(2)}`;
        case 'SG':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        case 'IE':
        case 'ZA':
        case 'AE':
        case 'SA':
          if (digits.length <= 2) return digits;
          if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
        case 'GR':
        case 'EG':
        case 'NG':
        case 'CO':
        case 'PH':
        case 'VN':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        case 'SE':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          if (digits.length <= 8) return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
        case 'CH':
          if (digits.length <= 2) return digits;
          if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
          if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
          return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
        case 'CL':
          if (digits.length <= 1) return digits;
          if (digits.length <= 5) return `${digits.slice(0, 1)} ${digits.slice(1)}`;
          return `${digits.slice(0, 1)} ${digits.slice(1, 5)} ${digits.slice(5)}`;
        case 'FI':
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        case 'KR':
          if (digits.length <= 3) return digits;
          if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
          return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
        case 'AT':
          if (digits.length <= 4) return digits;
          return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        case 'NZ':
          if (digits.length <= 3) return digits;
          return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        default:
          // Generic formatting: group in 3-4 digit chunks
          if (digits.length <= 3) return digits;
          if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
      }
    },
    handlePhoneInput(event) {
      let value = event.target.value;
      
      // Get country-specific rules
      const spec = COUNTRY_PHONE_SPECS[this.countryCode];
      const maxLength = spec ? spec.max : 15;
      
      // Extract digits only
      const digits = value.replace(/\D/g, '');
      
      if (!digits) {
        this.phoneNumber = '';
        this.emitValueToParent('');
        return;
      }
      
      // STRICT ENFORCEMENT: Limit digits to country's maximum length
      const limitedDigits = digits.slice(0, maxLength);
      
      // If user tries to enter more than max, prevent it
      if (digits.length > maxLength) {
        // Set to max allowed digits
        this.phoneNumber = this.formatPhoneNumber(limitedDigits, this.countryCode);
        this.emitValueToParent(this.fullPhoneNumber);
        return;
      }
      
      // Use libphonenumber-js for real-time formatting with country-specific rules
      try {
        // Use AsYouType for real-time formatting
        const formatter = new AsYouType(this.countryCode);
        const fullInput = `+${this.selectedCountry.dialCode}${limitedDigits}`;
        const formatted = formatter.input(fullInput);
        
        // Extract national number format (without country code)
        const phoneNumberObj = formatter.getNumber();
        if (phoneNumberObj) {
          // Verify country matches
          if (phoneNumberObj.country && phoneNumberObj.country !== this.countryCode) {
            // Country mismatch - use custom formatting to enforce country rules
            this.phoneNumber = this.formatPhoneNumber(limitedDigits, this.countryCode);
          } else {
            this.phoneNumber = phoneNumberObj.formatNational();
          }
        } else {
          // For incomplete numbers, extract the formatted national part
          const countryCodePattern = `+${this.selectedCountry.dialCode}`;
          let nationalFormatted = formatted.replace(countryCodePattern, '').trim();
          
          if (nationalFormatted) {
            // Use libphonenumber formatting if available
            this.phoneNumber = nationalFormatted;
          } else {
            // Fallback to custom formatting with strict country rules
            this.phoneNumber = this.formatPhoneNumber(limitedDigits, this.countryCode);
          }
        }
        
        // Emit the full phone number
        this.emitValueToParent(this.fullPhoneNumber);
      } catch (error) {
        // Fallback to custom formatting with strict country rules
        this.phoneNumber = this.formatPhoneNumber(limitedDigits, this.countryCode);
        this.emitValueToParent(this.fullPhoneNumber);
      }
    },
    selectCountry(country) {
      this.countryCode = country.code;
      this.isCountryDropdownOpen = false;
      this.countrySearchQuery = '';
      this.teardownDropdownListeners();

      // Reformat existing phone number with new country format
      if (this.phoneNumber) {
        const digits = this.phoneNumber.replace(/\D/g, '');
        this.phoneNumber = this.formatPhoneNumber(digits, country.code);
      }
      
      this.emitValueToParent(this.fullPhoneNumber);
      this.$emit('country-change', country.code);
    },
    toggleCountryDropdown(event) {
      if (this.disabled) return;
      event.stopPropagation();
      this.isCountryDropdownOpen = !this.isCountryDropdownOpen;

      if (this.isCountryDropdownOpen) {
        this.setupDropdownListeners();
        this.$nextTick(() => {
          this.positionDropdown();
          if (this.$refs.countrySearchInput) {
            this.$refs.countrySearchInput.focus();
          }
        });
      } else {
        this.countrySearchQuery = '';
        this.teardownDropdownListeners();
      }
    },
    positionDropdown() {
      if (!this.$refs.countryDropdown || !this.$el) return;
      
      const selectorEl = this.$el.querySelector('.phone-number-country-selector');
      if (!selectorEl) return;
      
      const rect = selectorEl.getBoundingClientRect();
      const dropdown = this.$refs.countryDropdown;
      const minWidth = 280;
      const dropdownWidth = Math.max(rect.width, minWidth);
      let left = rect.left;
      const viewportPadding = 8;

      if (left + dropdownWidth > window.innerWidth - viewportPadding) {
        left = Math.max(viewportPadding, window.innerWidth - dropdownWidth - viewportPadding);
      }

      let top = rect.bottom + 4;
      const estimatedHeight = Math.min(320, dropdown.offsetHeight || 300);
      if (top + estimatedHeight > window.innerHeight - viewportPadding) {
        top = Math.max(viewportPadding, rect.top - estimatedHeight - 4);
      }
      
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${top}px`;
      dropdown.style.left = `${left}px`;
      dropdown.style.width = `${dropdownWidth}px`;
      dropdown.style.zIndex = '10050';
    },
    filterCountries() {
      this.highlightedCountryIndex = -1;
    },
    handleCountryKeydown(event) {
      if (!this.isCountryDropdownOpen) return;
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.highlightedCountryIndex = Math.min(
            this.highlightedCountryIndex + 1,
            this.filteredCountries.length - 1
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.highlightedCountryIndex = Math.max(this.highlightedCountryIndex - 1, -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.highlightedCountryIndex >= 0) {
            this.selectCountry(this.filteredCountries[this.highlightedCountryIndex]);
          }
          break;
        case 'Escape':
          this.isCountryDropdownOpen = false;
          this.countrySearchQuery = '';
          this.teardownDropdownListeners();
          break;
      }
    },
    handleFocus() {
      this.$emit('focus');
    },
    handleBlur() {
      // When blurring, emit the current phone number to ensure parent has latest value
      if (this.fullPhoneNumber) {
        this.emitValueToParent(this.fullPhoneNumber);
      }
      this.$emit('blur');
    },
    handleKeydown(event) {
      this.$emit('keydown', event);
    },
    handleClickOutside(event) {
      // Use setTimeout to avoid closing immediately when opening
      setTimeout(() => {
        if (this.$el && !this.$el.contains(event.target) && !event.target.closest('.phone-number-country-dropdown')) {
          this.isCountryDropdownOpen = false;
          this.countrySearchQuery = '';
          this.teardownDropdownListeners();
        }
      }, 10);
    },
    handleScroll() {
      if (this.isCountryDropdownOpen) {
        this.positionDropdown();
      }
    },
    handleResize() {
      if (this.isCountryDropdownOpen) {
        this.positionDropdown();
      }
    },
    getFlagUrl(countryCode) {
      // Use flag-icons CDN for country flags
      // Format: https://flagcdn.com/w40/{country-code-lowercase}.png
      return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
    },
    isValidPhoneNumber() {
      const digits = this.phoneNumber.replace(/\D/g, '');
      
      // If empty, not valid
      if (!digits) return false;
      
      // Get country-specific rules
      const spec = COUNTRY_PHONE_SPECS[this.countryCode];
      
      // First check digit length against country rules
      if (spec) {
        // Must be within country's min and max length
        if (digits.length < spec.min || digits.length > spec.max) {
          return false;
        }
      } else {
        // Default: 7-15 digits for countries without specification
        if (digits.length < 7 || digits.length > 15) {
          return false;
        }
      }
      
      // Use libphonenumber-js validation
      try {
        const fullNumber = this.fullPhoneNumber;
        if (fullNumber) {
          // Try parsing with the selected country
          try {
            const phoneNumberObj = parsePhoneNumber(fullNumber, { defaultCountry: this.countryCode });
            
            if (phoneNumberObj) {
              const parsedCountry = phoneNumberObj.country;
              
              // If country is detected and doesn't match, it might still be valid
              // For example, Indian numbers starting with 0 might parse differently
              // Let's be lenient and check if it's valid format
              if (phoneNumberObj.isValid()) {
                // If valid, allow it (even if country detection is slightly off)
                return true;
              }
              
              // If country matches exactly, use its validation
              if (parsedCountry === this.countryCode) {
                return phoneNumberObj.isValid();
              }
            }
          } catch (parseError) {
            // If parsing fails, continue to try other validation methods
          }
          
          // Try direct validation
          const isValid = isValidPhoneNumber(fullNumber, { defaultCountry: this.countryCode });
          if (isValid) {
            return true;
          }
        }
      } catch (error) {
        // If validation fails, fallback to length-based validation
      }
      
      // Fallback: Use length-based validation as final check
      // If length matches country spec, consider it valid (lenient approach)
      if (spec) {
        return digits.length >= spec.min && digits.length <= spec.max;
      }
      
      return digits.length >= 7 && digits.length <= 15;
    }
  }
};
</script>

<style src="@/components/styles/PhoneNumberInput.css"></style>
